import type { Analysis, ClientWorkspaceCompany } from '../../../services/AnalysisApiClient';
import type { CompanySummaryCardProps } from '../../shared/CompanySummaryCard';

/** Prefer firm-linked analysis with a resolved company name; else any analysis with `data.companyName`. */
export function pickDealContextAnalysis(analyses: Analysis[]): Analysis | null {
  const withLink = analyses.find((a) => a.lawFirmId && a.companyId && String(a.data?.companyName || '').trim());
  if (withLink) return withLink;
  return analyses.find((a) => String(a.data?.companyName || '').trim()) ?? null;
}

/**
 * Row used to hydrate closing / workflow / law firm fields on {@link CompanySummaryCard}.
 * When `archivedForContext` is provided (Archived page), may fall back to an archived row if there are no active analyses.
 */
export function pickDisplayAnalysisForCompanyCard(
  analyses: Analysis[],
  dealContextAnalysis: Analysis | null,
  archivedForContext: Analysis[] | undefined,
  includeArchivedFallback: boolean
): Analysis | null {
  if (dealContextAnalysis) return dealContextAnalysis;
  if (analyses.length > 0) return analyses[0];
  if (includeArchivedFallback && archivedForContext?.length) {
    const withCompany = archivedForContext.find((a) => String(a.data?.companyName || '').trim());
    return withCompany ?? archivedForContext[0] ?? null;
  }
  return null;
}

export function hasContextListGateForCompanyCard(
  analysesLength: number,
  archivedLength: number | undefined,
  includeArchivedFallback: boolean
): boolean {
  return includeArchivedFallback ? analysesLength > 0 || (archivedLength ?? 0) > 0 : analysesLength > 0;
}

export function shouldShowClientCompanySummaryCard(params: {
  displayAnalysis: Analysis | null;
  loginType: string | undefined;
  hasContextListGate: boolean;
  workspaceCompanyName: string;
  analysisCompanyName: string;
  userCompanyName: string;
}): boolean {
  const { displayAnalysis, loginType, hasContextListGate, workspaceCompanyName, analysisCompanyName, userCompanyName } =
    params;
  return !!(
    displayAnalysis &&
    loginType === 'Client' &&
    hasContextListGate &&
    (workspaceCompanyName.trim().length > 0 ||
      analysisCompanyName.trim().length > 0 ||
      userCompanyName.trim().length > 0)
  );
}

export function buildCompanySummaryCardProps(
  workspaceCompany: ClientWorkspaceCompany | null,
  displayAnalysis: Analysis | null,
  userCompanyName: string | undefined
): CompanySummaryCardProps {
  return {
    companyName:
      String(workspaceCompany?.companyName || '').trim() ||
      String(displayAnalysis?.data?.companyName || '').trim() ||
      String(userCompanyName || '').trim() ||
      '—',
    closingDate: workspaceCompany?.closingDate || displayAnalysis?.data?.closingDate,
    workStatus: workspaceCompany?.workStatus || displayAnalysis?.data?.companyWorkStatus,
    dealStructure: workspaceCompany?.dealStructure ?? displayAnalysis?.data?.dealStructure,
    dealRole: workspaceCompany?.dealRole ?? displayAnalysis?.data?.dealRole,
    listingStatus: workspaceCompany?.listingStatus ?? displayAnalysis?.data?.listingStatus,
    lawFirmName: workspaceCompany?.lawFirmName || displayAnalysis?.lawFirmName,
  };
}
